import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useReactTable, getCoreRowModel } from '@tanstack/react-table'
import './index.css'

import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'

import ColumnsSelector from '../ColumnsSelection/ColumnSelector'
import columnsGenerator from '../ColumnsSelection/columnsGenerator'
import useZoom from '../useZoom'
import { applyRemovedColumnsToRows } from './applyRemovedColumnsToRows'
import { updatePayslipHiddenAllowanceColumns } from '../../../../../api/payroll'
import {
    getBillingColumnKey,
    getDefaultBillingColumnKeys,
} from './billingColumnVisibility'

function getColKey(col) {
    return getBillingColumnKey(col)
}

function normalizeCellValue(value) {
    if (value == null) return ''
    if (Array.isArray(value)) {
        return value
            .map((item) => normalizeCellValue(item))
            .filter((item) => item !== '')
            .join(', ')
    }
    if (typeof value === 'object') {
        if (typeof value.amount === 'number' || typeof value.amount === 'string') {
            const n = Number(value.amount)
            return Number.isFinite(n) ? n : ''
        }
        return ''
    }
    return value
}

function computePickerSelectionFromHidden(templatesForPicker, allowanceKeys, hiddenRaw) {
    if (!templatesForPicker?.length) return []
    const hidden = new Set((hiddenRaw || []).map((k) => String(k).trim().toLowerCase()))
    const keys = getDefaultBillingColumnKeys(templatesForPicker)
    return keys.filter((k) => {
        const kl = k.trim().toLowerCase()
        const isGranularAllowance = allowanceKeys.some(
            (a) => String(a).trim().toLowerCase() === kl
        )
        if (isGranularAllowance) return !hidden.has(kl)
        return true
    })
}

/** Picker never lists Personnel allowances; table injects that column when any granular personnel allowance is selected. */
function buildOrderedTableKeysFromPicker(pickerKeys, payrollColumns, personnelAllowanceNames) {
    const personnelSelected =
        personnelAllowanceNames?.length > 0 &&
        personnelAllowanceNames.some((n) =>
            pickerKeys.some((k) => String(k).trim().toLowerCase() === String(n).trim().toLowerCase())
        )
    const payrollKeySet = new Set(payrollColumns.map((c) => getColKey(c)).filter(Boolean))
    const selectedKeys = new Set(
        pickerKeys.filter(
            (k) =>
                payrollKeySet.has(k) && String(k).trim().toLowerCase() !== 'personnel allowances'
        )
    )
    if (personnelSelected) {
        const hasPCol = payrollColumns.some((c) => getColKey(c) === 'Personnel allowances')
        if (hasPCol) selectedKeys.add('Personnel allowances')
    }
    const orderedTableKeys = []
    payrollColumns.forEach((col) => {
        const k = getColKey(col)
        if (k && selectedKeys.has(k)) orderedTableKeys.push(k)
    })
    return orderedTableKeys
}

function buildTableStateFromPickerKeys(pickerKeys, sourceRows, payrollColumns, personnelAllowanceNames) {
    const tableKeys = buildOrderedTableKeysFromPicker(pickerKeys, payrollColumns, personnelAllowanceNames)
    const visible = new Set(tableKeys)
    const newSelectedColumns = columnsGenerator(payrollColumns, tableKeys)
    const removedColumns = payrollColumns.filter((col) => {
        const k = getColKey(col)
        return k && !visible.has(k)
    })
    const newRows = applyRemovedColumnsToRows(sourceRows, payrollColumns, removedColumns)
    return { newSelectedColumns, newRows }
}

/**
 * payrollColumns — full column defs
 * pickerColumnTemplates — menu items (payroll columns + synthetic personnel names for payslip toggles)
 * allowanceColumnKeys — granular keys (personnel allowance names + store/group names)
 * hiddenAllowanceColumnKeys — keys the user hid via column picker (persisted on payroll)
 */
const SevenStarPayroll = ({
    data = [],
    payrollColumns = [],
    pickerColumnTemplates = null,
    personnelAllowanceNames = [],
    onPayrollRowsUpdate = () => { },
    allowanceColumnKeys = [],
    hiddenAllowanceColumnKeys = [],
    onHiddenAllowancePersisted,
}) => {
    const { id: payrollId } = useParams()
    const [tableData, setTableData] = useState(data)
    const [selectedColumns, setSelectedColumns] = useState(payrollColumns)
    const [pickerSelectedKeys, setPickerSelectedKeys] = useState([])
    const { zoom, handleZoomIn, handleZoomOut, resetZoom } = useZoom()
    const saveHiddenTimer = useRef(null)
    const serverHiddenSig = useMemo(
        () => (hiddenAllowanceColumnKeys || []).slice().sort().join('|'),
        [hiddenAllowanceColumnKeys]
    )

    const templatesForPicker = useMemo(() => {
        const base = pickerColumnTemplates ?? payrollColumns
        return base.filter((c) => getColKey(c) !== 'Personnel allowances')
    }, [pickerColumnTemplates, payrollColumns])

    const validPickerSelectedKeys = useMemo(() => {
        const allowed = new Set(templatesForPicker.map((t) => getColKey(t)).filter(Boolean))
        return pickerSelectedKeys.filter((k) => allowed.has(k))
    }, [templatesForPicker, pickerSelectedKeys])

    const table = useReactTable({
        data: tableData,
        columns: selectedColumns,
        getCoreRowModel: getCoreRowModel(),
    })

    /** Apply server-persisted hidden allowance columns when payroll loads or hidden list changes. */
    useEffect(() => {
        if (!payrollColumns.length || !data.length) return

        const hiddenRaw = hiddenAllowanceColumnKeys || []

        const syncTemplates = templatesForPicker

        if (!allowanceColumnKeys.length) {
            const nextPickerKeys = getDefaultBillingColumnKeys(syncTemplates)
            setPickerSelectedKeys(nextPickerKeys)
            const { newSelectedColumns, newRows } = buildTableStateFromPickerKeys(
                nextPickerKeys,
                data,
                payrollColumns,
                personnelAllowanceNames
            )
            setSelectedColumns(newSelectedColumns)
            setTableData(newRows)
            return
        }

        const nextPickerKeys = computePickerSelectionFromHidden(
            syncTemplates,
            allowanceColumnKeys,
            hiddenRaw
        )
        setPickerSelectedKeys(nextPickerKeys)

        const { newSelectedColumns, newRows } = buildTableStateFromPickerKeys(
            nextPickerKeys,
            data,
            payrollColumns,
            personnelAllowanceNames
        )
        setSelectedColumns(newSelectedColumns)
        setTableData(newRows)
    }, [
        serverHiddenSig,
        payrollColumns,
        data,
        allowanceColumnKeys,
        templatesForPicker,
        personnelAllowanceNames,
    ])

    const persistHiddenAllowanceKeys = useCallback(
        (fullPickerSelectedKeys) => {
            if (!payrollId || !allowanceColumnKeys.length) return
            const selectedLower = new Set(
                (fullPickerSelectedKeys || []).map((k) => String(k).trim().toLowerCase())
            )
            const hiddenKeys = allowanceColumnKeys.filter(
                (k) => !selectedLower.has(String(k).trim().toLowerCase())
            )
            if (saveHiddenTimer.current) clearTimeout(saveHiddenTimer.current)
            saveHiddenTimer.current = setTimeout(() => {
                updatePayslipHiddenAllowanceColumns(payrollId, hiddenKeys)
                    .then(() => {
                        onHiddenAllowancePersisted?.(hiddenKeys)
                        window.dispatchEvent(
                            new CustomEvent('payroll-payslip-settings-updated', {
                                detail: { payrollId },
                            })
                        )
                    })
                    .catch((err) => {
                        console.error('Failed to save payslip allowance column visibility', err)
                    })
            }, 450)
        },
        [payrollId, allowanceColumnKeys, onHiddenAllowancePersisted]
    )

    const onSelectColumnsChange = useCallback(
        (_generatedFromSelector, fullPickerKeys) => {
            const pickerKeys = fullPickerKeys ?? []
            setPickerSelectedKeys(pickerKeys)
            const { newSelectedColumns, newRows } = buildTableStateFromPickerKeys(
                pickerKeys,
                data,
                payrollColumns,
                personnelAllowanceNames
            )
            setSelectedColumns(newSelectedColumns)
            setTableData(newRows)
            onPayrollRowsUpdate(newRows)
            persistHiddenAllowanceKeys(pickerKeys)
        },
        [payrollColumns, data, onPayrollRowsUpdate, persistHiddenAllowanceKeys, personnelAllowanceNames]
    )

    const rows = table.getRowModel().rows;

    const columns = selectedColumns;
    const columnSums = useMemo(() => {
        const sums = {};
        columns.forEach(col => {
            const ak = getColKey(col)
            if (ak) {
                sums[ak] = tableData.reduce((acc, row) => acc + (Number(row[ak]) || 0), 0);
            }
            if (typeof col.accessorFn === 'function') {
                sums[col.header] = tableData.reduce((acc, row) => acc + (Number(col.accessorFn(row)) || 0), 0);
            }
        });
        return sums;
    }, [columns, tableData])

    return (
        <Paper>
            <Stack direction='row' sx={{ p: 1, bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }} justifyContent='space-between'>
                <Typography variant="h6" component="div" >
                    Payroll Table
                </Typography>


                <Stack direction='row' spacing={1} >
                    <Tooltip title="Zoom In">
                        <IconButton
                            size="small"
                            sx={{
                                border: '1px solid #e5e7eb',
                                borderRadius: 2,
                                '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                            }}
                            onClick={handleZoomIn}
                        >
                            <ZoomInIcon fontSize="small" sx={{ color: '#6b7280' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Zoom out">
                        <IconButton
                            onClick={handleZoomOut}
                            size="small"
                            sx={{
                                border: '1px solid #e5e7eb',
                                borderRadius: 2,
                                '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                            }}
                        >
                            <ZoomOutIcon fontSize="small" sx={{ color: '#6b7280' }} />
                        </IconButton>
                    </Tooltip>

                    <Button onClick={resetZoom}>Reset Zoom</Button>
                </Stack>


            </Stack>

            <Stack p={1} >
                <Typography variant='subtitle2' color='text.secondary'>Select columns to be displayed here</Typography>
                <Typography variant='caption' color='text.secondary' display="block" sx={{ mb: 0.5 }}>
                    Loan columns are hidden by default because they are excluded from billing, but they can still be selected for reference. Hiding an allowance (including each personnel allowance name) hides matching lines on payslips and adjusts gross/net on the payslip. The consolidated Personnel allowances column is shown when at least one personnel allowance is selected; it is not listed separately here.
                </Typography>
                <ColumnsSelector
                    selectedColumns={validPickerSelectedKeys}
                    columnTemplates={templatesForPicker}
                    columnTemplatesForTable={payrollColumns}
                    onColumnsChange={onSelectColumnsChange}
                />
            </Stack>
            <div className='payroll-table__container'>
                <div className="payroll-table__wrapper">
                    <table id='payroll_table' className="payroll-table" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
                        <thead className="payroll-table__header">
                            <tr>
                                {columns.map((col, idx) => (
                                    <th key={getColKey(col) || col.id || idx}>
                                        {col.header}
                                    </th>
                                ))}
                            </tr>

                        </thead>
                        <tbody className="payroll-table__body">
                            {/**store name here */}

                            {rows.map((row) => (
                                <tr key={row.id}>
                                    {columns.map((col, idx) => {
                                        let value;
                                        let textAlign = 'left'
                                        const ak = getColKey(col)
                                        if (typeof col.accessorFn === 'function') {
                                            value = col.accessorFn(row.original);
                                        } else if (ak) {
                                            value = row.original[ak];
                                        } else {
                                            value = '';
                                        }

                                        value = normalizeCellValue(value)
                                        value = Number(value)
                                            ? Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                            : value

                                        if (typeof parseFloat(value) == 'number')
                                            textAlign = 'right'

                                        if (ak === 'index') {
                                            textAlign = 'center'
                                            value = parseFloat(Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 }))
                                        }

                                        if (ak === 'employee_name'){
                                            textAlign = 'left'
                                            value = String(value).toUpperCase()
                                        }



                                        return (
                                            <td key={ak || col.id || idx} style={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                textAlign: textAlign,
                                                maxWidth: col.header === 'Employee Name' ? '150px' : 'auto'
                                            }}>
                                                {value}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}


                            <tr style={{
                                backgroundColor: 'yellow',
                                fontWeight: 'bold'
                            }}>
                                {columns.map((col, idx) => {
                                    let value = '';
                                    const ak = getColKey(col)

                                    if (ak) {
                                        value = columnSums[ak] || 0;
                                    }
                                    if (typeof col.accessorFn === 'function') {
                                        value = columnSums[col.header] || 0;
                                    }

                                    if (ak === 'index' || ak === 'employee_name') value = ''
                                    return (
                                        <td key={ak || col.id || idx} style={{
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                            textAlign: typeof value === 'number' ? 'right' : 'left',
                                            maxWidth: col.header === 'Employee Name' ? '150px' : 'auto'
                                        }}>
                                            {Number(value) ? Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value}
                                        </td>
                                    );
                                })}
                            </tr>

                        </tbody>
                    </table>
                </div>
            </div>
        </Paper>
    );
}

export default SevenStarPayroll
